import { BMPrimitives } from "@traveloka/district-bloom";
import { useRef, useState } from "react";
import { Animated, LayoutChangeEvent } from "react-native";

type Params = {
  isAccordion: boolean;
  isDefaultOpen: boolean;
  extraHeight?: number;
  initContentHeight?: number;
};

export default function useCollapsibleAnimation({
  isAccordion,
  isDefaultOpen,
  extraHeight = 0,
  initContentHeight = 0,
}: Params) {
  const [isOpen, setIsOpen] = useState(isAccordion ? isDefaultOpen : true);
  const [contentHeight, setContentHeight] = useState(initContentHeight);

  // Animation setup
  const rotateChevronAnim = useRef(
    new Animated.Value(isDefaultOpen ? 1 : 0)
  ).current;
  const heightAnim = useRef(new Animated.Value(isOpen ? 1 : 0)).current;

  // Interpolations for animations
  const heightInterpolate = heightAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, contentHeight + extraHeight],
  });
  const opacityInterpolate = heightAnim.interpolate({
    inputRange: [0, 0.75, 1],
    outputRange: [0, 0, 1],
  });
  const translateYInterpolate = heightAnim.interpolate({
    inputRange: [0, 0.75, 1],
    outputRange: [-BMPrimitives.BMSize.sm, 0, 0],
  });
  const rotateInterpolate = rotateChevronAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  // Animation style
  const contentWrapperAnimatedStyle = {
    height: heightInterpolate,
  };
  const contentAnimatedStyle = {
    opacity: opacityInterpolate,
    transform: [
      {
        translateY: translateYInterpolate,
      },
    ],
  };
  const chevronAnimatedStyle = {
    transform: [{ rotate: rotateInterpolate }],
  };

  function onLayoutContentWrapper(event: LayoutChangeEvent) {
    const { height } = event.nativeEvent.layout;
    // Prevent height to be changed to zero
    if (!height) return;

    setContentHeight(height);
  }

  function onToggleAnimate() {
    Animated.timing(rotateChevronAnim, {
      toValue: isOpen ? 0 : 1,
      duration: BMPrimitives.BMAnimation.duration.quick,
      useNativeDriver: true,
    }).start();
    Animated.timing(heightAnim, {
      toValue: isOpen ? 0 : 1,
      duration: BMPrimitives.BMAnimation.duration.quick,
      useNativeDriver: false,
    }).start();
    setIsOpen((prev) => !prev);
  }

  return {
    isOpen,
    contentAnimatedStyle,
    contentWrapperAnimatedStyle,
    chevronAnimatedStyle,
    onLayoutContentWrapper,
    onToggleAnimate,
  };
}
